import React, { useState, useEffect } from 'react';
import { BadgeDollarSign, Loader2, Ticket, Send, Clock } from 'lucide-react';
import { apiClient } from '../../api/client';

const money = (cents) => `£${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

/* School dashboard — beta countdown, post-beta plan chooser, active license view. */
const LicenseCard = () => {
  const [data, setData] = useState(null);
  const [students, setStudents] = useState(275);
  const [requesting, setRequesting] = useState(null);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    apiClient.request('/api/school-licensing/my').then(setData).catch(() => {});
  }, []);

  const requestPlan = async (tier) => {
    setRequesting(tier);
    try {
      await apiClient.request('/api/school-licensing/request', {
        method: 'POST',
        body: JSON.stringify({ tier, student_count: tier === 'per_student' ? Number(students) : null }),
      });
      setRequested(true);
    } catch (e) { alert(e.message); }
    setRequesting(null);
  };

  if (!data) return null;

  if (data.license) {
    const lic = data.license;
    const unpaid = (data.invoices || []).find((i) => i.status === 'issued');
    return (
      <div className="bg-white rounded-xl p-5 mb-6" style={{ border: '1px solid #E0E0E0' }} data-testid="school-license-card">
        <div className="flex items-center gap-2 mb-2">
          <BadgeDollarSign size={16} style={{ color: '#059669' }} />
          <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Your HiBFF License</h3>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>ACTIVE</span>
        </div>
        <p className="text-xs" style={{ color: '#44403A' }}>
          {lic.tier_label} — {money(lic.annual_amount_cents)}/year · {lic.billable_students} students · 5-year term until {lic.term_end?.slice(0, 10)}
        </p>
        {unpaid && (
          <p className="text-xs mt-2 font-semibold" style={{ color: '#D97706' }} data-testid="invoice-due-line">
            Invoice {unpaid.number} — {money(unpaid.amount_cents)} due {unpaid.due_at?.slice(0, 10)} (net 30)
          </p>
        )}
        <p className="text-xs mt-2 flex items-center gap-1.5" style={{ color: '#7B4FD8' }} data-testid="community-code-line">
          <Ticket size={12} /> Share with your families: code <span className="font-mono font-bold">{lic.community_code}</span> = 50% off a direct HiBFF subscription
        </p>
      </div>
    );
  }

  const beta = data.beta_days_left;
  return (
    <div className="bg-white rounded-xl p-5 mb-6" style={{ border: '1px solid #E0E0E0' }} data-testid="school-beta-card">
      <div className="flex items-center gap-2 mb-1">
        <Clock size={16} style={{ color: beta !== null && beta <= 7 ? '#DC2626' : '#F5831F' }} />
        <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Free Beta</h3>
        {beta !== null && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: beta <= 7 ? '#FEF2F2' : '#FFF7ED', color: beta <= 7 ? '#DC2626' : '#D97706' }} data-testid="beta-days-left">
            {beta} day{beta === 1 ? '' : 's'} left
          </span>
        )}
        <span className="text-xs" style={{ color: '#6B6257' }}>{data.students_enrolled}/{data.student_limit} student seats used</span>
      </div>
      {requested ? (
        <p className="text-sm font-semibold mt-2" style={{ color: '#059669' }} data-testid="license-requested-msg">
          ✓ Request sent — the HiBFF team will confirm your license and email your invoice (net 30 days).
        </p>
      ) : (
        <>
          <p className="text-xs mb-3" style={{ color: '#44403A' }}>When your beta ends, choose the plan that fits your city:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 rounded-lg" style={{ border: '1px solid #E8E8E8', backgroundColor: '#FAFAFA' }}>
              <p className="text-xs font-bold" style={{ color: '#1A1209' }}>School License</p>
              <p className="text-lg font-bold" style={{ color: '#7B4FD8' }}>Call for Special Pricing</p>
              <p className="text-[11px]" style={{ color: '#6B6257' }}>Every school&apos;s rate is quoted individually · Billed annually · 5-year revolving license · volume agreements for districts</p>
              <div className="flex items-center gap-2 mt-2">
                <input type="number" min={1} value={students} onChange={(e) => setStudents(e.target.value)}
                  className="w-20 px-2 py-1 border rounded text-xs" style={{ borderColor: '#E0E0E0' }} data-testid="request-student-count" />
                <button onClick={() => requestPlan('per_student')} disabled={!!requesting}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: '#7B4FD8' }} data-testid="request-per-student">
                  {requesting === 'per_student' ? <Loader2 size={12} className="animate-spin inline" /> : <><Send size={10} className="inline mr-1" />Request this plan</>}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LicenseCard;
